import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import type { DecisionListRequest, DecisionRow } from "@/api/client.js";
import { apiDeleteDecision } from "@/api/client.js";
import type { AppShellOutletContext } from "@/components/layout/AppShell.js";
import { BouncersCard } from "@/features/decisions/BouncersCard.js";
import { DecisionFiltersBar } from "@/features/decisions/DecisionFiltersBar.js";
import { DecisionsTable } from "@/features/decisions/DecisionsTable.js";
import { UnbanConfirmDialog } from "@/features/decisions/UnbanConfirmDialog.js";
import { useBouncersQuery } from "@/features/decisions/useBouncersQuery.js";
import { decisionsQueryKey, useDecisionsQuery } from "@/features/decisions/useDecisionsQuery.js";
import { IPInspectorCard } from "@/features/ip-filter/IPInspectorCard.js";
import type { DecisionMatchScope } from "@/features/ip-filter/match.js";

const defaultListRequest = (): DecisionListRequest => ({ limit: 100, type: "ban" });

export function DashboardPage() {
  const { requestWhitelist, requestBan } = useOutletContext<AppShellOutletContext>();
  const qc = useQueryClient();
  const [appliedList, setAppliedList] = useState<DecisionListRequest>(defaultListRequest);
  const [draftList, setDraftList] = useState<DecisionListRequest>(defaultListRequest);

  const decisionsQuery = useDecisionsQuery(appliedList);
  const { data, isLoading, isError, error } = decisionsQuery;
  const bouncersQuery = useBouncersQuery();

  const [unbanRow, setUnbanRow] = useState<DecisionRow | null>(null);

  const deleteMut = useMutation({
    mutationFn: apiDeleteDecision,
    onSuccess: async () => {
      toast.success("Decision removed");
      setUnbanRow(null);
      await qc.invalidateQueries({ queryKey: decisionsQueryKey });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  const listType = appliedList.type ?? "ban";
  const matchScope: DecisionMatchScope = listType === "all" ? "all" : "bans";
  const pageTitle = listType === "all" ? "Decisions" : "Bans";
  const pageSubtitle =
    listType === "all"
      ? "CrowdSec decisions from the server for the selected filters."
      : "Active ban decisions from the server.";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{pageTitle}</h1>
        <p className="text-sm text-muted-foreground">{pageSubtitle}</p>
      </div>

      <DecisionFiltersBar
        draft={draftList}
        onDraftChange={setDraftList}
        onApply={() => {
          setAppliedList({ ...draftList });
        }}
      />

      <BouncersCard query={bouncersQuery} />

      <IPInspectorCard rows={data} onBanThisIp={(ip) => requestBan(ip)} matchScope={matchScope} />

      <DecisionsTable
        data={data}
        isLoading={isLoading}
        isError={isError}
        error={error instanceof Error ? error : null}
        listType={listType}
        matchScope={matchScope}
        onUnban={(row) => setUnbanRow(row)}
        onUseIp={(ip) => requestWhitelist(ip)}
      />

      <UnbanConfirmDialog
        open={unbanRow !== null}
        onOpenChange={(open) => {
          if (!open) setUnbanRow(null);
        }}
        row={unbanRow}
        isPending={deleteMut.isPending}
        onConfirm={() => {
          if (unbanRow) void deleteMut.mutateAsync(unbanRow.id);
        }}
      />
    </div>
  );
}
